/**
 * Per-tab tour progress, kept in sessionStorage so a mid-tour refresh
 * resumes at the same step/frame. New tab → no progress → fresh tour.
 *
 * Versioning is part of the payload because tour content can change
 * between releases; restoring step 4 of v1 in a v2 deploy would land on
 * the wrong card. On version mismatch we clear the progress and let
 * the auto-start path do its job from step 0.
 *
 * All accessors swallow storage errors so Safari private mode (and
 * similar throwing environments) cannot break the tour.
 */

const KEY = 'lr.onboarding.progress';

export interface TourProgress {
  stepIndex: number;
  frameIndex: number;
  version: number;
}

const isValidProgress = (raw: unknown): raw is TourProgress => {
  if (!raw || typeof raw !== 'object') return false;
  const r = raw as Record<string, unknown>;
  return (
    typeof r.stepIndex === 'number' &&
    Number.isFinite(r.stepIndex) &&
    r.stepIndex >= 0 &&
    typeof r.frameIndex === 'number' &&
    Number.isFinite(r.frameIndex) &&
    r.frameIndex >= 0 &&
    typeof r.version === 'number' &&
    Number.isFinite(r.version) &&
    r.version >= 0
  );
};

export const readProgress = (): TourProgress | null => {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isValidProgress(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const writeProgress = (p: TourProgress): void => {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    // Best-effort. The tour still works in-memory; we just lose
    // the resume-after-refresh affordance.
  }
};

export const clearProgress = (): void => {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // Best-effort.
  }
};
